import type { ChangeEvent as ReactChangeEvent, JSX } from 'react';
import {
  Box, Radio, RadioGroup, styled,
} from '@mui/material';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  CHART_LANE_COLOURS,
  CHART_LANE_CONTROL_ICON_SIZE,
  INFORMED_MODE,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  ChartAssay,
  InformedMode,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { getAssayLabel } from '@/utils/functions/getAssayLabel';
import AssayMarker from './AssayMarker';
import { LaneCheckbox, LaneRow, LaneRowName } from './ChartLane.styles';

const INFORMED_MODES: Array<{ value: InformedMode; label: string }> = [
  { value: INFORMED_MODE.INFORMED, label: 'Informed' },
  { value: INFORMED_MODE.UNINFORMED, label: 'Uninformed' },
];

const LANE_ROW_GAP_PX = 6;
const MODE_RADIO_ICON_SIZE = 15;

const Root = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const ModeGroup = styled(RadioGroup)({
  paddingLeft: `${CHART_LANE_CONTROL_ICON_SIZE + LANE_ROW_GAP_PX}px`,
  gap: '2px',
});

const ModeRadio = styled(Radio)({
  padding: 0,
  color: corePalette.grey200,
  '&.Mui-checked': {
    color: CHART_LANE_COLOURS.toggleOn,
  },
  '& .MuiSvgIcon-root': {
    fontSize: MODE_RADIO_ICON_SIZE,
  },
});

const ModeLabel = styled(CustomTypography)({
  color: corePalette.grey100,
});

interface IProps {
  analyte: string;
  assay: ChartAssay;
  colour: string;
  isSelected: boolean;
  mode: InformedMode;
  onToggle: (analyte: string, assay: ChartAssay) => void;
  onSetMode: (analyte: string, assay: ChartAssay, mode: InformedMode) => void;
}

export default function AssayFilterRow({
  analyte,
  assay,
  colour,
  isSelected,
  mode,
  onToggle,
  onSetMode,
}: IProps): JSX.Element {
  const label = getAssayLabel(assay);

  const handleToggle = (): void => onToggle(analyte, assay);
  const handleModeChange = (
    unused: ReactChangeEvent<HTMLInputElement>,
    value: string,
  ): void => onSetMode(analyte, assay, value as InformedMode);

  return (
    <Root>
      <LaneRow>
        <LaneCheckbox
          checked={isSelected}
          onChange={handleToggle}
          disableRipple
          inputProps={{ 'aria-label': `Toggle ${label} for ${analyte}` }}
        />
        <AssayMarker assay={assay} colour={colour} isDetected />
        <LaneRowName
          truncate
          tooltipText={label}
          variant="bodyTiny"
          fontWeight="medium"
        >
          {label}
        </LaneRowName>
      </LaneRow>

      {isSelected && (
        <ModeGroup value={mode} onChange={handleModeChange}>
          {INFORMED_MODES.map((option) => (
            <LaneRow key={option.value}>
              <ModeRadio
                value={option.value}
                disableRipple
                inputProps={{ 'aria-label': `${option.label} ${label} for ${analyte}` }}
              />
              <ModeLabel variant="bodyTiny">{option.label}</ModeLabel>
            </LaneRow>
          ))}
        </ModeGroup>
      )}
    </Root>
  );
}
